import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "w-full flex flex-col lg:flex-row-reverse gap-8 lg:gap-6" }
const _hoisted_2 = {
  class: "grow",
  "data-test-id": "account_overview_title"
}
const _hoisted_3 = {
  key: 0,
  class: "mt-4 mb-6"
}
const _hoisted_4 = { class: "flex flex-col gap-4" }
const _hoisted_5 = { class: "text-base" }
const _hoisted_6 = { class: "pr-3" }
const _hoisted_7 = {
  key: 0,
  class: "inline",
  xmlns: "http://www.w3.org/2000/svg",
  width: "18",
  height: "19",
  viewBox: "0 0 18 19",
  fill: "none"
}

import { ref, computed } from 'vue';
import { useAccountStore, usePageStore } from '@/store';
import MembershipCard from '@/components/MembershipCard/MembershipCard.vue';
import MembershipCardModal from '@/components/MembershipCard/MembershipCardModal.vue';
import { CardVariant } from '@/enums/membership-card-variant.enum';
import { getScreenSize } from '@/utils/dom-helpers';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import ProgressBar from '@/components/ProgressBar/ProgressBar.vue';
import { ComponentFields } from '@sitecore-jss/sitecore-jss-vue';
import { LoyaltyLevelYears } from '@/enums/loyalty-level-years.enum';
import { LoyaltyLevels } from '@/enums/loyalty-levels.enum';
import { hasLoyaltyLevel } from '@/utils/loyalty-level';


export default /*@__PURE__*/_defineComponent({
  __name: 'BannerMega',
  props: {
    fields: {}
  },
  setup(__props: any) {

const props = __props;
const pageStore = usePageStore();
const accountStore = useAccountStore();

const copied = ref(false);
const membershipCardModalModalEl = ref<InstanceType<typeof MembershipCardModal>>();

const membershipNumber = computed(() => accountStore.identityDetails?.membershipCardNumber);

const memberLoyaltyLevel = computed(
  () => accountStore.identityDetails?.membershipLoyalty?.toLowerCase() || LoyaltyLevels.None
);

const memberSince = computed(
  () => accountStore.identityDetails?.tenureDate ?? accountStore.identityDetails?.membershipJoined
);

const membershipCardVariant = computed(() => {
  const { xs, sm, xse } = getScreenSize();
  return xs.value || sm.value || xse.value ? CardVariant.THUMBNAIL : CardVariant.FRONT;
});

const memberDetailsBindings = computed(() => {
  if (!accountStore.identityDetails?.firstName) return props.fields;
  const t = props.fields?.title?.value?.replace('{{firstname}}', accountStore.identityDetails.firstName);
  return {
    ...props.fields,
    title: { value: t },
  };
});

/**
 * We don't show progress bar for free2go member
 */
const showProgressBar = computed(() => {
  return hasLoyaltyLevel(memberLoyaltyLevel.value);
});

const membershipCardModalData = computed(() => pageStore.membershipCardModalData ?? {});

const calculatePercent = (joinDate: Date, nextLevelStart: number) => {
  const timeFromCurrentStartToNow = Date.now() - joinDate.getTime();
  const nextLevelDate = new Date();
  nextLevelDate.setFullYear(joinDate.getFullYear() + nextLevelStart);
  const timeFromCurrentStartToNextLevel = nextLevelDate.getTime() - joinDate.getTime();
  const actualPercent =
    timeFromCurrentStartToNow / (1000 * 60 * 60 * 24) / (timeFromCurrentStartToNextLevel / (1000 * 60 * 60 * 24));
  // Faked value between 30% and 98% to always show some progress
  return 0.3 + (0.98 - 0.3) * actualPercent;
};

const getLoyaltyProgress = (currentLevel: string, memberSince: Date) => {
  if (currentLevel == LoyaltyLevels.None || !memberSince) return 0;

  let percent = 1;
  if (currentLevel == LoyaltyLevels.Blue) {
    percent = calculatePercent(memberSince, LoyaltyLevelYears.BronzeYearStart);
  } else if (currentLevel == LoyaltyLevels.Bronze) {
    percent = calculatePercent(memberSince, LoyaltyLevelYears.SilverYearStart);
  } else if (currentLevel == LoyaltyLevels.Silver) {
    percent = calculatePercent(memberSince, LoyaltyLevelYears.GoldYearStart);
  } else if (currentLevel == LoyaltyLevels.Gold) {
    percent = calculatePercent(memberSince, LoyaltyLevelYears.Gold50YearStart);
  } else if (currentLevel == LoyaltyLevels.Gold50) {
    percent = 1;
  }

  return Math.floor(percent * 100);
};

const getNextLevel = (currentLevel: string) => {
  if (currentLevel == LoyaltyLevels.Blue) {
    return 'Bronze';
  } else if (currentLevel == LoyaltyLevels.Bronze) {
    return 'Silver';
  } else if (currentLevel == LoyaltyLevels.Silver) {
    return 'Gold';
  } else if (currentLevel == LoyaltyLevels.Gold) {
    return 'Gold 50';
  }
  return '';
};

const copyMemberNumber = (memberNumber: string | null) => {
  if (!memberNumber) return;
  try {
    navigator.clipboard.writeText(memberNumber);

    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 6000);
  } catch (err) {
    console.error('Failed to copy member number: ', err);
  }
};

const showCard = () => {
  if (membershipCardModalModalEl?.value) {
    membershipCardModalModalEl.value.showModal();
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", null, [
        _createVNode(MembershipCard, {
          variant: membershipCardVariant.value,
          level: memberLoyaltyLevel.value,
          extraClasses: "lg:cursor-pointer hover:shadow-1xl hover:opacity-90",
          onClick: showCard
        }, null, 8, ["variant", "level"])
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(PanelTitle, _normalizeProps(_guardReactiveProps(memberDetailsBindings.value)), null, 16),
        (showProgressBar.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(ProgressBar, {
                value: getLoyaltyProgress(memberLoyaltyLevel.value, memberSince.value),
                memberColour: memberLoyaltyLevel.value,
                memberSince: memberSince.value,
                nextLevel: getNextLevel(memberLoyaltyLevel.value)
              }, null, 8, ["value", "memberColour", "memberSince", "nextLevel"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("p", _hoisted_5, [
            _cache[3] || (_cache[3] = _createElementVNode("strong", { class: "pr-1 font-medium" }, "Member:", -1)),
            _cache[4] || (_cache[4] = _createTextVNode()),
            _createElementVNode("span", _hoisted_6, _toDisplayString(membershipNumber.value), 1),
            (copied.value)
              ? (_openBlock(), _createElementBlock("svg", _hoisted_7, _cache[1] || (_cache[1] = [
                  _createElementVNode("g", { "clip-path": "url(#clip0_2556_6339)" }, [
                    _createElementVNode("path", {
                      d: "M9 0.860352C4.0275 0.860352 0 4.88785 0 9.86035C0 14.8329 4.0275 18.8604 9 18.8604C13.9725 18.8604 18 14.8329 18 9.86035C18 4.88785 13.9725 0.860352 9 0.860352ZM12.9488 7.8241L8.64 13.1116C8.47125 13.3029 8.24625 13.4154 8.01 13.4154C7.77375 13.4154 7.54875 13.3141 7.38 13.1229L5.09625 10.6254C4.75875 10.2991 4.75875 9.7141 5.09625 9.3541C5.2425 9.18535 5.45625 9.0841 5.68125 9.07285C5.92875 9.07285 6.165 9.1741 6.33375 9.3541L7.965 11.1204L11.6213 6.62035C11.7675 6.4291 12.015 6.30535 12.24 6.30535H12.465V6.3391C12.6225 6.3616 12.7575 6.44035 12.87 6.55285C13.23 6.86785 13.275 7.45285 12.96 7.8241H12.9488Z",
                      fill: "#128750"
                    })
                  ], -1),
                  _createElementVNode("defs", null, [
                    _createElementVNode("clipPath", { id: "clip0_2556_6339" }, [
                      _createElementVNode("rect", {
                        width: "18",
                        height: "18",
                        fill: "white",
                        transform: "translate(0 0.860352)"
                      })
                    ])
                  ], -1)
                ])))
              : (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: "text-base-link text-racq-blue underline font-medium",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (copyMemberNumber(membershipNumber.value)))
                }, _cache[2] || (_cache[2] = [
                  _createTextVNode(" Copy"),
                  _createElementVNode("svg", {
                    class: "inline ml-1",
                    xmlns: "http://www.w3.org/2000/svg",
                    width: "18",
                    height: "18",
                    viewBox: "0 0 18 18",
                    fill: "none"
                  }, [
                    _createElementVNode("path", {
                      d: "M9.22266 3.01465C10.6421 3.15862 11.7498 4.35708 11.75 5.81445V6.16699H13.1582L13.4453 6.18164C14.8646 6.32574 15.9725 7.52416 15.9727 8.98145V12.8525L15.958 13.1396C15.8235 14.4645 14.7702 15.5179 13.4453 15.6523L13.1582 15.667H9.28711C7.82986 15.6668 6.63145 14.5589 6.4873 13.1396L6.47266 12.8525V12.5H5.06445C3.60708 12.4998 2.40862 11.3921 2.26465 9.97266L2.25 9.68555V5.81445C2.25021 4.26016 3.51016 3.00021 5.06445 3H8.93555L9.22266 3.01465ZM9.28711 8.27832C8.89876 8.27853 8.58419 8.5931 8.58398 8.98145V12.8525C8.58435 13.2407 8.89887 13.5555 9.28711 13.5557H13.1582C13.5464 13.5554 13.861 13.2407 13.8613 12.8525V8.98145C13.8611 8.59316 13.5465 8.27862 13.1582 8.27832H9.28711ZM5.06445 5.11133C4.67611 5.11153 4.36153 5.42611 4.36133 5.81445V9.68555C4.3615 10.0739 4.67609 10.3885 5.06445 10.3887H6.47266V8.98145C6.47286 7.42716 7.73282 6.1672 9.28711 6.16699H9.63867V5.81445C9.63847 5.42609 9.32392 5.1115 8.93555 5.11133H5.06445Z",
                      fill: "#0864BC"
                    })
                  ], -1)
                ])))
          ]),
          _createElementVNode("p", { class: "lg:hidden" }, [
            _createElementVNode("button", {
              class: "btn btn-primary btn-md",
              onClick: showCard
            }, "Membership Card")
          ])
        ])
      ])
    ]),
    _createVNode(MembershipCardModal, {
      ref_key: "membershipCardModalModalEl",
      ref: membershipCardModalModalEl,
      membershipCardModal: membershipCardModalData.value
    }, null, 8, ["membershipCardModal"])
  ], 64))
}
}

})